import { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import Input from 'common-ui/input';
import Modal from 'common-ui/modal';
import Select from 'common-ui/select/Select';
import { getAdministrativeRegions } from 'features/newsales/newsalesActions';

import { ModalFormContent } from '../Addresses.styles';

const CityModal = ({ isOpen, onClose, onSubmit, city, countries, loading }) => {
  const dispatch = useDispatch();
  const [name, setName] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [regions, setRegions] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (city) {
      setName(city.name || '');
      if (city.countryId && countries) {
        const country = countries.find((c) => c.id === city.countryId);
        if (country) {
          setSelectedCountry({ value: country.id, label: country.name });
          fetchRegions(city.countryId);
        }
      }
    } else {
      setName('');
      setSelectedCountry(null);
      setSelectedRegion(null);
      setRegions([]);
    }
    setError('');
  }, [city, countries, isOpen]);

  const fetchRegions = async (countryId) => {
    if (!countryId) {
      setRegions([]);
      return;
    }
    setLoadingRegions(true);
    try {
      const result = await dispatch(
        getAdministrativeRegions({
          countryId,
          limit: 1000,
        })
      );
      if (result.payload && result.payload.data) {
        setRegions(result.payload.data);
        if (city && city.districtId) {
          const region = result.payload.data.find((r) => r.id === city.districtId);
          if (region) {
            setSelectedRegion({ value: region.id, label: region.name });
          }
        }
      }
    } catch (error) {
      setRegions([]);
    } finally {
      setLoadingRegions(false);
    }
  };

  const handleCountryChange = (option) => {
    setSelectedCountry(option);
    setSelectedRegion(null);
    setError('');
    if (option) {
      fetchRegions(option.value);
    } else {
      setRegions([]);
    }
  };

  const handleSubmit = () => {
    if (!name.trim()) {
      setError('City/Village name is required');
      return;
    }

    if (!selectedCountry) {
      setError('Please select a country');
      return;
    }

    if (!selectedRegion) {
      setError('Please select a region');
      return;
    }

    onSubmit({
      name: name.trim(),
      countryId: selectedCountry.value,
      districtId: selectedRegion.value,
    });
  };

  const handleClose = () => {
    setName('');
    setSelectedCountry(null);
    setSelectedRegion(null);
    setRegions([]);
    setError('');
    onClose();
  };

  const countryOptions = countries
    ? countries.map((country) => ({
        value: country.id,
        label: country.name,
      }))
    : [];

  const regionOptions = regions.map((region) => ({
    value: region.id,
    label: region.name,
  }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      onOk={handleSubmit}
      title={city ? 'Edit City/Village' : 'Create City/Village'}
      okText={city ? 'Update' : 'Create'}
      cancelText="Cancel"
      footer
      closeIcon
      width="450px"
      onOkLoading={loading}
    >
      <ModalFormContent>
        <Select
          label="Country"
          req
          value={selectedCountry}
          onChange={handleCountryChange}
          options={countryOptions}
          placeholder="Select a country"
          isClearable
        />
        <Select
          label="Region"
          req
          value={selectedRegion}
          onChange={(option) => {
            setSelectedRegion(option);
            setError('');
          }}
          options={regionOptions}
          placeholder={
            loadingRegions
              ? 'Loading regions...'
              : selectedCountry
                ? 'Select a region'
                : 'Select a country first'
          }
          isClearable
          isDisabled={!selectedCountry || loadingRegions}
        />
        <Input
          label="City/Village Name"
          required
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setError('');
          }}
          error={error}
          fullWidth
          placeholder="Enter city/village name"
        />
      </ModalFormContent>
    </Modal>
  );
};

export default CityModal;
